"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.EventLogger = void 0;
const date_fns_1 = require("date-fns");
const js_cookie_1 = __importDefault(require("js-cookie"));
const uuid_1 = require("uuid");
const util_1 = require("./util");
const ANONYMOUS_USER_ID_KEY = "sourcegraphAnonymousUid";
const COHORT_ID_KEY = "sourcegraphCohortId";
const FIRST_SOURCE_URL_KEY = "sourcegraphSourceUrl";
const LAST_SOURCE_URL_KEY = 'sourcegraphRecentSourceUrl';
const DEVICE_ID_KEY = 'sourcegraphDeviceId';
const DEVICE_SESSION_ID_KEY = 'sourcegraphSessionId';
const INSERT_ID_KEY = 'sourcegraphInsertId';
const ORIGINAL_REFERRER_KEY = 'originalReferrer';
const MKTO_ORIGINAL_REFERRER_KEY = '_mkto_referrer';
const SESSION_REFERRER_KEY = 'sessionReferrer';
const SESSION_FIRST_URL_KEY = 'sessionFirstUrl';
class EventLogger {
    constructor(sourcegraphURL) {
        this.sourcegraphURL = sourcegraphURL;
        this.anonymousUserID = "";
        this.deviceID = '';
        this.deviceSessionID = '';
        this.insertID = '';
        this.cookieSettings = {
            // 365 days expiry, but renewed on activity.
            expires: 365,
            // Enforce HTTPS
            secure: true,
            // We only read the cookie with JS so we don't need to send it cross-site nor on initial page requests.
            // However, we do need it on page redirects when users sign up via OAuth, hence using the Lax policy.
            // https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Set-Cookie/SameSite
            sameSite: "Lax",
            // Specify the Domain attribute to ensure subdomains (about.sourcegraph.com) can receive this cookie.
            // https://developer.mozilla.org/en-US/docs/Web/HTTP/Cookies#define_where_cookies_are_sent
            domain: 'sourcegraph.com',
        };
        this.deviceSessionCookieSettings = {
            // ~30 minutes expiry, but renewed on activity.
            expires: 0.0208,
            // Enforce HTTPS
            secure: true,
            // We only read the cookie with JS so we don't need to send it cross-site nor on initial page requests.
            // However, we do need it on page redirects when users sign up via OAuth, hence using the Lax policy.
            // https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Set-Cookie/SameSite
            sameSite: "Lax",
            // Specify the Domain attribute to ensure subdomains (about.sourcegraph.com) can receive this cookie.
            // https://developer.mozilla.org/en-US/docs/Web/HTTP/Cookies#define_where_cookies_are_sent
            domain: 'sourcegraph.com',
        };
        this.initializeLogParameters();
    }
    /**
     * Log a `ViewStaticPage`event with the provided URL as a parameter.
     *
     * @param {string} url the URL to be logged.
     */
    log(eventName, eventProperties, publicArgument) {
        return __awaiter(this, void 0, void 0, function* () {
            this.logToConsole(eventName, eventProperties, publicArgument);
            fetch(`${this.sourcegraphURL}/.api/graphql`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({
                    query: `
            mutation logEvent(
                $name: String!
                $userCookieID: String!
                $cohortID: String
                $deviceID: String
                $firstSourceURL: String!
                $lastSourceURL: String
                $deviceSessionID: String
                $insertID: String
                $referrer: String
                $originalReferrer: String
                $sessionReferrer: String
                $sessionFirstURL: String
                $url: String!
                $source: EventSource!
                $argument: String
                $publicArgument: String
            ) {
                logEvent(
                    event: $name,
                    userCookieID: $userCookieID,
                    cohortID: $cohortID,
                    deviceID: $deviceID,
                    firstSourceURL: $firstSourceURL,
                    lastSourceURL: $lastSourceURL,
                    deviceSessionID: $deviceSessionID,
                    insertID: $insertID,
                    referrer: $referrer,
                    originalReferrer: $originalReferrer,
                    sessionReferrer: $sessionReferrer,
                    sessionFirstURL: $sessionFirstURL,
                    url: $url,
                    source: $source
                    argument: $argument
                    publicArgument: $publicArgument
                ) {
                    alwaysNil
                }
            }
        `,
                    variables: {
                        name: eventName,
                        userCookieID: this.anonymousUserID,
                        cohortID: this.cohortID || null,
                        deviceID: this.getDeviceID(),
                        firstSourceURL: this.getFirstSourceURL(),
                        lastSourceURL: this.getLastSourceURL(),
                        deviceSessionID: this.getDeviceSessionID(),
                        insertID: this.getInsertID(),
                        referrer: this.getReferrer(),
                        originalReferrer: this.getOriginalReferrer(),
                        sessionReferrer: this.getSessionReferrer(),
                        sessionFirstURL: this.getSessionFirstURL(),
                        url: window.location.href,
                        source: "STATICWEB",
                        argument: eventProperties
                            ? JSON.stringify(eventProperties)
                            : null,
                        publicArgument: publicArgument
                            ? JSON.stringify(publicArgument)
                            : null,
                    },
                }),
            }).catch((error) => {
                // Swallow errors. This should not be necessary for the Sourcegraph Cloud GraphQL API (that
                // this file logs to), which should always be up to date, but end users should not be subjected
                // to error messages.
                console.log(error);
            });
        });
    }
    logToConsole(eventName, eventProperties, publicArgument) {
        if (!localStorage || localStorage.getItem("eventLogDebug") !== "true") {
            return;
        }
        console.debug("%cEVENT %s", "color: #aaa", eventName, eventProperties, publicArgument);
    }
    /**
     * Gets the anonymous user ID and cohort ID of the user from cookies.
     * If user doesn't have an anonymous user ID yet, a new one is generated, along with
     * a cohort ID of the week the user first visited.
     *
     * If the user already has an anonymous user ID before the introduction of cohort IDs,
     * the user will not have a cohort ID.
     *
     * If user had an anonymous user ID in localStorage, it will be migrated to cookies.
     */
    initializeLogParameters() {
        let anonymousUserID = js_cookie_1.default.get(ANONYMOUS_USER_ID_KEY);
        let cohortID = js_cookie_1.default.get(COHORT_ID_KEY);
        let deviceID = js_cookie_1.default.get(DEVICE_ID_KEY);
        let insertID = js_cookie_1.default.get(INSERT_ID_KEY);
        let deviceSessionID = js_cookie_1.default.get(DEVICE_SESSION_ID_KEY);
        if (!anonymousUserID) {
            anonymousUserID = (0, uuid_1.v4)();
            cohortID = this.getPreviousMonday(new Date());
        }
        // Use cookies instead of localStorage so that the ID can be shared with subdomains (about.sourcegraph.com).
        // Always set to renew expiry and migrate from localStorage
        js_cookie_1.default.set(ANONYMOUS_USER_ID_KEY, anonymousUserID, this.cookieSettings);
        localStorage.removeItem(ANONYMOUS_USER_ID_KEY);
        if (cohortID) {
            js_cookie_1.default.set(COHORT_ID_KEY, cohortID, this.cookieSettings);
        }
        if (!deviceID || deviceID === '') {
            // If device ID does not exist, use the anonymous user ID value so these are consolidated.
            deviceID = anonymousUserID;
        }
        js_cookie_1.default.set(DEVICE_ID_KEY, deviceID, this.cookieSettings);
        if (!deviceSessionID || deviceSessionID === '') {
            // If device ID does not exist, use the anonymous user ID value so these are consolidated.
            deviceSessionID = anonymousUserID;
        }
        js_cookie_1.default.set(DEVICE_SESSION_ID_KEY, deviceSessionID, this.deviceSessionCookieSettings);
        if (!insertID || insertID === '') {
            insertID = this.getInsertID();
        }
        js_cookie_1.default.set(INSERT_ID_KEY, insertID, this.cookieSettings);
        this.deviceSessionID = deviceSessionID;
        this.anonymousUserID = anonymousUserID;
        this.cohortID = cohortID;
        this.deviceID = deviceID;
    }
    getFirstSourceURL() {
        const firstSourceURL = this.firstSourceURL ||
            js_cookie_1.default.get(FIRST_SOURCE_URL_KEY) ||
            window.location.href;
        // Use cookies instead of localStorage so that the ID can be shared across subdomains (about.sourcegraph.com).
        // Always set to renew expiry
        js_cookie_1.default.set(FIRST_SOURCE_URL_KEY, firstSourceURL, this.cookieSettings);
        localStorage.removeItem(FIRST_SOURCE_URL_KEY);
        this.firstSourceURL = firstSourceURL;
        return firstSourceURL;
    }
    getLastSourceURL() {
        // The cookie value gets overwritten each time a user visits a *.sourcegraph.com property. This code
        // lives in Google Tag Manager.
        const lastSourceURL = js_cookie_1.default.get(LAST_SOURCE_URL_KEY) || this.lastSourceURL || window.location.href;
        // Use cookies instead of localStorage so that the ID can be shared with subdomains (about.sourcegraph.com).
        // Always set to renew expiry
        js_cookie_1.default.set(LAST_SOURCE_URL_KEY, lastSourceURL, this.cookieSettings);
        localStorage.removeItem(LAST_SOURCE_URL_KEY);
        this.lastSourceURL = lastSourceURL;
        return lastSourceURL;
    }
    // Device ID is a require field for Amplitude events.
    // https://developers.amplitude.com/docs/http-api-v2
    getDeviceID() {
        let deviceID = js_cookie_1.default.get(DEVICE_ID_KEY) || this.deviceID;
        if (!deviceID || deviceID === '') {
            deviceID = this.anonymousUserID;
        }
        // Use cookies instead of localStorage so that the ID can be shared with subdomains (about.sourcegraph.com).
        // Always set to renew expiry
        js_cookie_1.default.set(DEVICE_ID_KEY, deviceID, this.cookieSettings);
        localStorage.removeItem(DEVICE_ID_KEY);
        this.deviceID = deviceID;
        return deviceID;
    }
    // Insert ID is used to deduplicate events in Amplitude.
    // https://developers.amplitude.com/docs/http-api-v2#optional-keys
    getInsertID() {
        let insertID = js_cookie_1.default.get(INSERT_ID_KEY) || this.insertID;
        if (!insertID || insertID === '') {
            insertID = (0, uuid_1.v4)();
        }
        this.insertID = insertID;
        return insertID;
    }
    getDeviceSessionID() {
        let deviceSessionID = js_cookie_1.default.get(DEVICE_SESSION_ID_KEY) || this.deviceSessionID;
        if (!deviceSessionID || deviceSessionID === '') {
            // If device ID does not exist, use the anonymous user ID value so these are consolidated.
            deviceSessionID = this.anonymousUserID;
        }
        // Use cookies instead of localStorage so that the ID can be shared with subdomains (about.sourcegraph.com).
        // Always set to renew expiry
        js_cookie_1.default.set(DEVICE_SESSION_ID_KEY, deviceSessionID, this.deviceSessionCookieSettings);
        this.deviceSessionID = deviceSessionID;
        return deviceSessionID;
    }
    getOriginalReferrer() {
        // Gets the original referrer from the cookie or if it doesn't exist, the mkto_referrer from the URL.
        let originalReferrer = this.originalReferrer ||
            js_cookie_1.default.get(ORIGINAL_REFERRER_KEY) ||
            js_cookie_1.default.get(MKTO_ORIGINAL_REFERRER_KEY) ||
            document.referrer;
        try {
            // 🚨 SECURITY: If the referrer is a valid Sourcegraph.com URL,
            // only send the hostname instead of the whole URL to avoid
            // leaking private repository names and files into our data.
            const url = new URL(originalReferrer);
            const regexp = new RegExp('.sourcegraph.com');
            if (url.hostname === 'sourcegraph.com' || regexp.test(url.hostname)) {
                this.originalReferrer = '';
                js_cookie_1.default.set(ORIGINAL_REFERRER_KEY, this.originalReferrer, this.cookieSettings);
                return this.originalReferrer;
            }
            js_cookie_1.default.set(ORIGINAL_REFERRER_KEY, originalReferrer, this.cookieSettings);
            return originalReferrer;
        }
        catch (_a) {
            this.originalReferrer = '';
            js_cookie_1.default.set(ORIGINAL_REFERRER_KEY, this.originalReferrer, this.cookieSettings);
            return this.originalReferrer;
        }
    }
    getSessionReferrer() {
        // Gets the session referrer from the cookie
        const sessionReferrer = this.sessionReferrer || js_cookie_1.default.get(SESSION_REFERRER_KEY) || document.referrer;
        const regexp = new RegExp('.sourcegraph.com');
        try {
            // 🚨 SECURITY: If the referrer is a valid Sourcegraph.com URL,
            // only send the hostname instead of the whole URL to avoid
            // leaking private repository names and files into our data.
            const url = new URL(sessionReferrer);
            if (url.hostname === 'sourcegraph.com' || regexp.test(url.hostname)) {
                this.sessionReferrer = '';
                js_cookie_1.default.set(SESSION_REFERRER_KEY, this.sessionReferrer, this.deviceSessionCookieSettings);
                return this.sessionReferrer;
            }
            js_cookie_1.default.set(SESSION_REFERRER_KEY, sessionReferrer, this.deviceSessionCookieSettings);
            return sessionReferrer;
        }
        catch (_a) {
            this.sessionReferrer = '';
            js_cookie_1.default.set(SESSION_REFERRER_KEY, this.sessionReferrer, this.deviceSessionCookieSettings);
            return this.sessionReferrer;
        }
    }
    getSessionFirstURL() {
        const sessionFirstURL = this.sessionFirstURL || js_cookie_1.default.get(SESSION_FIRST_URL_KEY) || location.href;
        const redactedURL = (0, util_1.redactSensitiveInfoFromAppURL)(sessionFirstURL);
        // Use cookies instead of localStorage so that the ID can be shared with subdomains (about.sourcegraph.com).
        // Always set to renew expiry and migrate from localStorage
        js_cookie_1.default.set(SESSION_FIRST_URL_KEY, redactedURL, this.deviceSessionCookieSettings);
        this.sessionFirstURL = redactedURL;
        return this.sessionFirstURL;
    }
    getPreviousMonday(date) {
        return (0, date_fns_1.formatISO)((0, date_fns_1.startOfWeek)(date, { weekStartsOn: 1 }), {
            representation: "date",
        });
    }
    getReferrer() {
        const referrer = document.referrer;
        let regexp = new RegExp('.sourcegraph.com');
        try {
            // 🚨 SECURITY: If the referrer is a valid Sourcegraph.com URL,
            // only send the hostname instead of the whole URL to avoid
            // leaking private repository names and files into our data.
            const url = new URL(referrer);
            if (url.hostname === "sourcegraph.com" || regexp.test(url.hostname)) {
                return url.hostname;
            }
            return referrer;
        }
        catch (_a) {
            return "";
        }
    }
}
exports.EventLogger = EventLogger;
//# sourceMappingURL=index.js.map